import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiCheck, FiSmartphone, FiMonitor, FiSave, FiRefreshCw } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';
import StorefrontSections from '../../components/dashboard/StorefrontSections';

const fonts = [
  'Inter', 'Poppins', 'Roboto', 'Open Sans', 'Montserrat', 'Playfair Display', 'Lato',
  'Cairo', 'Tajawal', 'Almarai', 'Alexandria', 'Noto Kufi Arabic', 'Readex Pro', 'IBM Plex Sans Arabic',
];

export default function ThemeEditor() {
  const { store, reloadStore, lang } = useOutletContext<{ store: any; reloadStore: () => void; lang: DashLang }>();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [saving, setSaving] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [activeTab, setActiveTab] = useState<'colors' | 'typography' | 'layout' | 'sections'>('colors');

  const [form, setForm] = useState<any>({});
  const [layoutSettings, setLayoutSettings] = useState<any>({});

  const t = (en: string, ar: string) => lang === 'ar' ? ar : en;

  useEffect(() => {
    if (store) {
      setForm({
        primary_color: store.primary_color || '#635BFF',
        secondary_color: store.secondary_color || '#14213D',
        accent_color: store.accent_color || '#0F9F9A',
        text_color: store.text_color || '#14213D',
        background_color: store.background_color || '#FFFFFF',
        font_family: store.font_family || 'Inter',
        layout: store.layout || 'modern',
        name: store.name || '',
        name_ar: store.name_ar || '',
      });
      setLayoutSettings(store.layout_settings || {});
    }
  }, [store]);

  // Send live updates to iframe
  const sendToPreview = (updates: any) => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'theme-editor-update', ...updates },
      '*'
    );
  };

  // Update form and send to preview
  const updateForm = (key: string, value: any) => {
    const next = { ...form, [key]: value };
    setForm(next);
    sendToPreview({ form: next, layoutSettings });
  };

  const updateLayout = (key: string, value: any) => {
    const next = { ...layoutSettings, [key]: value };
    setLayoutSettings(next);
    sendToPreview({ form, layoutSettings: next });
  };

  // Send initial state once iframe loads
  const handleIframeLoad = () => {
    setTimeout(() => {
      sendToPreview({ form, layoutSettings });
    }, 500);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/stores/${store.id}`, form);
      await api.put(`/stores/${store.id}/layout`, layoutSettings);
      toast.success(dt('design_saved', lang));
      reloadStore();
    } catch (err: any) {
      toast.error(err.response?.data?.message || dt('error_saving', lang));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (store) {
      setForm({
        primary_color: store.primary_color || '#635BFF',
        secondary_color: store.secondary_color || '#14213D',
        accent_color: store.accent_color || '#0F9F9A',
        text_color: store.text_color || '#14213D',
        background_color: store.background_color || '#FFFFFF',
        font_family: store.font_family || 'Inter',
        layout: store.layout || 'modern',
        name: store.name || '',
        name_ar: store.name_ar || '',
      });
      setLayoutSettings(store.layout_settings || {});
      // Reload iframe
      if (iframeRef.current) {
        iframeRef.current.src = iframeRef.current.src;
      }
    }
  };

  if (!store) return null;

  const storeUrl = store.custom_domain
    ? `https://${store.custom_domain}`
    : `${window.location.origin}/store/${store.slug}`;

  const colorFields = [
    { key: 'primary_color', label: t('Primary Color', 'اللون الأساسي') },
    { key: 'secondary_color', label: t('Secondary Color', 'اللون الثانوي') },
    { key: 'accent_color', label: t('Accent Color', 'لون التمييز') },
    { key: 'text_color', label: t('Text Color', 'لون النص') },
    { key: 'background_color', label: t('Background Color', 'لون الخلفية') },
  ];

  const fontSizeFields = [
    { key: 'font_size_title', label: t('Store Title', 'عنوان المتجر'), defaultVal: 24, min: 14, max: 60 },
    { key: 'font_size_menu', label: t('Menu / Categories', 'القائمة / الفئات'), defaultVal: 14, min: 10, max: 30 },
    { key: 'font_size_product_name', label: t('Product Name', 'اسم المنتج'), defaultVal: 16, min: 10, max: 40 },
    { key: 'font_size_product_price', label: t('Product Price', 'سعر المنتج'), defaultVal: 16, min: 10, max: 40 },
    { key: 'font_size_body', label: t('Body Text', 'النص العام'), defaultVal: 14, min: 10, max: 30 },
  ];

  const toggleFields = [
    { key: 'show_header_banner', label: t('Header Banner', 'شعار الرأس') },
    { key: 'show_categories_bar', label: t('Categories Bar', 'شريط الفئات') },
    { key: 'show_search', label: t('Search Bar', 'شريط البحث') },
    { key: 'show_featured', label: t('Featured Products', 'المنتجات المميزة') },
    { key: 'show_store_info', label: t('Store Info', 'معلومات المتجر') },
    { key: 'show_social_links', label: t('Social Links', 'روابط التواصل') },
    { key: 'show_delivery_info', label: t('Delivery Info', 'معلومات التوصيل') },
    { key: 'show_working_hours', label: t('Working Hours', 'ساعات العمل') },
    { key: 'sticky_nav', label: t('Sticky Navigation', 'تنقل ثابت'), defaultOff: true },
    { key: 'sticky_filters', label: t('Sticky Filters', 'فلاتر ثابتة'), defaultOff: true },
    { key: 'infinite_scroll', label: t('Infinite Scroll', 'تحميل تلقائي'), defaultOff: true },
  ];

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', margin: '-24px', overflow: 'hidden' }}>
      {/* Controls Panel */}
      <div style={{
        width: 340, minWidth: 340, background: '#fff', borderInlineEnd: '1px solid var(--gray-100)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Panel Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--gray-100)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
            {t('Theme Editor', 'محرر المظهر')}
          </h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleReset}
              title={t('Reset', 'إعادة تعيين')}
              style={{
                padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: 6,
                background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 13, color: 'var(--gray-600)',
              }}
            >
              <FiRefreshCw size={14} />
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '6px 14px', border: 'none', borderRadius: 6,
                background: 'var(--primary)', color: 'white', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600,
              }}
            >
              <FiSave size={14} /> {saving ? t('Saving...', 'حفظ...') : t('Save', 'حفظ')}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', borderBottom: '1px solid var(--gray-100)', padding: '0 20px',
        }}>
          {([
            { id: 'colors', label: t('Colors', 'الألوان') },
            { id: 'typography', label: t('Typography', 'الخط') },
            { id: 'layout', label: t('Layout', 'التخطيط') },
            { id: 'sections', label: t('Sections', 'الأقسام') },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
                color: activeTab === tab.id ? 'var(--primary)' : 'var(--gray-500)',
                borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {activeTab === 'colors' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {colorFields.map(({ key, label }) => (
                <div key={key}>
                  <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block', color: 'var(--gray-700)' }}>
                    {label}
                  </label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="color"
                      value={form[key] || '#000000'}
                      onChange={(e) => updateForm(key, e.target.value)}
                      style={{ width: 40, height: 34, border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer', padding: 2 }}
                    />
                    <input
                      className="form-input"
                      value={form[key] || ''}
                      onChange={(e) => updateForm(key, e.target.value)}
                      style={{ flex: 1, fontSize: 13, padding: '6px 10px' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'typography' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Font Family */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block', color: 'var(--gray-700)' }}>
                  {t('Font Family', 'نوع الخط')}
                </label>
                <select
                  className="form-input"
                  value={form.font_family}
                  onChange={(e) => updateForm('font_family', e.target.value)}
                  style={{ fontSize: 13 }}
                >
                  {fonts.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>

              {/* Store Name */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block', color: 'var(--gray-700)' }}>
                  {t('Store Name (EN)', 'اسم المتجر (إنجليزي)')}
                </label>
                <input
                  className="form-input"
                  value={form.name || ''}
                  onChange={(e) => updateForm('name', e.target.value)}
                  style={{ fontSize: 13 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block', color: 'var(--gray-700)' }}>
                  {t('Store Name (AR)', 'اسم المتجر (عربي)')}
                </label>
                <input
                  className="form-input"
                  value={form.name_ar || ''}
                  onChange={(e) => updateForm('name_ar', e.target.value)}
                  style={{ fontSize: 13 }}
                />
              </div>

              {/* Font Sizes */}
              <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--gray-700)' }}>
                  {t('Font Sizes', 'أحجام الخط')}
                </h4>
                {fontSizeFields.map(({ key, label, defaultVal, min, max }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 12, flex: 1, color: 'var(--gray-600)' }}>{label}</span>
                    <input
                      type="range"
                      min={min} max={max} step={1}
                      value={layoutSettings[key] || defaultVal}
                      onChange={(e) => updateLayout(key, Number(e.target.value))}
                      style={{ width: 90 }}
                    />
                    <span style={{ fontSize: 11, color: 'var(--gray-400)', minWidth: 30, textAlign: 'center' }}>
                      {layoutSettings[key] || defaultVal}px
                    </span>
                  </div>
                ))}
              </div>

              {/* Logo Size */}
              <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--gray-700)' }}>
                  {t('Logo Size', 'حجم الشعار')}
                </h4>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, color: 'var(--gray-500)' }}>{t('Width', 'العرض')}</label>
                    <input type="number" className="form-input" min="20" max="400" placeholder="Auto"
                      value={layoutSettings.logo_width || ''}
                      onChange={(e) => updateLayout('logo_width', e.target.value ? parseInt(e.target.value) : null)}
                      style={{ fontSize: 13, padding: '4px 8px' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, color: 'var(--gray-500)' }}>{t('Height', 'الارتفاع')}</label>
                    <input type="number" className="form-input" min="20" max="200" placeholder="Auto"
                      value={layoutSettings.logo_height || ''}
                      onChange={(e) => updateLayout('logo_height', e.target.value ? parseInt(e.target.value) : null)}
                      style={{ fontSize: 13, padding: '4px 8px' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'layout' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Layout Selection */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, display: 'block', color: 'var(--gray-700)' }}>
                  {t('Store Layout', 'تخطيط المتجر')}
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {['modern', 'classic', 'minimal', 'elegant', 'food', 'advanced', 'boutique'].map(id => (
                    <button
                      key={id}
                      onClick={() => updateForm('layout', id)}
                      style={{
                        padding: '10px 12px', borderRadius: 8,
                        border: `2px solid ${form.layout === id ? 'var(--primary)' : 'var(--gray-100)'}`,
                        background: form.layout === id ? 'rgba(230, 57, 70, 0.03)' : 'white',
                        cursor: 'pointer', fontSize: 13, fontWeight: form.layout === id ? 600 : 400,
                        color: form.layout === id ? 'var(--primary)' : 'var(--gray-600)',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      {form.layout === id && <FiCheck size={14} />}
                      {dt(id, lang)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Toggles */}
              <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--gray-700)' }}>
                  {t('Sections & Behavior', 'الأقسام والسلوك')}
                </h4>
                {toggleFields.map(({ key, label, defaultOff }: any) => (
                  <label key={key} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                    cursor: 'pointer', fontSize: 13, color: 'var(--gray-700)',
                  }}>
                    <input
                      type="checkbox"
                      checked={defaultOff ? (layoutSettings[key] ?? false) : (layoutSettings[key] ?? true)}
                      onChange={(e) => updateLayout(key, e.target.checked)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'sections' && (
            <StorefrontSections
              storeId={store.id}
              lang={lang}
              layoutSettings={layoutSettings}
              onChange={updateLayout}
            />
          )}
        </div>
      </div>

      {/* Preview Panel */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--gray-50)' }}>
        {/* Preview Toolbar */}
        <div style={{
          padding: '8px 20px', borderBottom: '1px solid var(--gray-100)', background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <button
            onClick={() => setDevice('desktop')}
            style={{
              padding: '6px 12px', border: '1px solid var(--gray-200)', borderRadius: 6,
              background: device === 'desktop' ? 'var(--gray-100)' : 'white', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 13,
            }}
          >
            <FiMonitor size={14} /> {t('Desktop', 'سطح المكتب')}
          </button>
          <button
            onClick={() => setDevice('mobile')}
            style={{
              padding: '6px 12px', border: '1px solid var(--gray-200)', borderRadius: 6,
              background: device === 'mobile' ? 'var(--gray-100)' : 'white', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 13,
            }}
          >
            <FiSmartphone size={14} /> {t('Mobile', 'جوال')}
          </button>
        </div>

        {/* iframe */}
        <div style={{
          flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          padding: 20, overflow: 'auto',
        }}>
          <iframe
            ref={iframeRef}
            src={`${storeUrl}?theme_editor=1`}
            onLoad={handleIframeLoad}
            title="Store Preview"
            style={{
              width: device === 'mobile' ? 390 : '100%',
              height: device === 'mobile' ? 844 : '100%',
              maxWidth: '100%',
              border: device === 'mobile' ? '8px solid #222' : '1px solid var(--gray-200)',
              borderRadius: device === 'mobile' ? 32 : 8,
              background: '#fff',
              boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
              transition: 'width 0.3s, height 0.3s',
            }}
          />
        </div>
      </div>
    </div>
  );
}
